const isObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isMeaningfulString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

interface AggregatedResult {
  raw: string;
  events: any[];
  response?: string;
  responseChunks?: string[];
  responseLines?: string[];
  responseParagraphs?: string[];
  conversationId?: string | null;
  conversationID?: string | null;
  deploymentConversationId?: string | null;
  externalSessionId?: string | null;
  [key: string]: any;
}

export class AbacusStreamParser {
  private static readonly IMPLICIT_SPACE_WORDS = new Set<string>([
    'a',
    'an',
    'and',
    'are',
    'as',
    'at',
    'be',
    'but',
    'by',
    'could',
    'for',
    'found',
    'from',
    'has',
    'have',
    'he',
    'her',
    'his',
    'how',
    'i',
    'if',
    'in',
    'into',
    'is',
    'it',
    'its',
    'just',
    'like',
    'may',
    'me',
    'mean',
    'no',
    'not',
    'of',
    'on',
    'or',
    'our',
    'she',
    'should',
    'so',
    'that',
    'the',
    'their',
    'there',
    'these',
    'this',
    'those',
    'to',
    'was',
    'we',
    'were',
    'what',
    'when',
    'where',
    'which',
    'who',
    'will',
    'with',
    'would',
    'you',
    'your',
    'any',
  ]);

  private buffer = '';

  private raw = '';

  private responseBuilder = '';

  private responseComparable = '';

  private events: any[] = [];

  private responseChunks: string[] = [];

  private responseChunkSet = new Set<string>();

  private responseLines: string[] = [];

  private responseLineSet = new Set<string>();

  private paragraphFlags: boolean[] = [];

  private aggregated: AggregatedResult = {
    raw: '',
    events: [],
  };

  ingest(chunk: string): AggregatedResult {
    if (chunk) {
      this.buffer += chunk;
      this.raw += chunk;
    }

    this.processBuffer();

    return this.buildAggregated();
  }

  finalize(): AggregatedResult {
    if (this.buffer.trim().length > 0) {
      // Try to process any remaining buffered content.
      this.processSegment(this.buffer);
      this.buffer = '';
    }

    return this.buildAggregated();
  }

  private processBuffer() {
    if (!this.buffer) {
      return;
    }

    const segments = this.buffer.split(/\n\n/);
    this.buffer = segments.pop() ?? '';

    segments.forEach(segment => this.processSegment(segment));
  }

  private tryParseJson(value: string) {
    if (!value) {
      return undefined;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return undefined;
    }
  }

  private processSegment(segment: string) {
    if (!segment) {
      return;
    }

    const trimmed = segment.trim();
    if (!trimmed) {
      return;
    }

    const dataLines = trimmed
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .filter(line => line.startsWith('data:'))
      .map(line => line.replace(/^data:\s*/, '').trim())
      .filter(Boolean);

    if (dataLines.length === 0) {
      const fallbackLines = trimmed
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean);

      let parsedAny = false;

      fallbackLines.forEach(line => {
        const parsedLine = this.tryParseJson(line);
        if (parsedLine !== undefined) {
          parsedAny = true;
          this.events.push(parsedLine);
          this.collectFromSource(parsedLine);
        }
      });

      if (!parsedAny) {
        const parsedSegment = this.tryParseJson(trimmed);
        if (parsedSegment !== undefined) {
          this.events.push(parsedSegment);
          this.collectFromSource(parsedSegment);
        }
      }

      return;
    }

    dataLines.forEach(payload => {
      if (!payload || payload === '[DONE]') {
        return;
      }

      let parsed: any = null;
      try {
        parsed = JSON.parse(payload);
      } catch (error) {
        parsed = payload;
      }

      this.events.push(parsed);
      this.collectFromSource(parsed);
    });
  }

  private pushChunk(
    value: unknown,
    {trackAsLine = false}: {trackAsLine?: boolean} = {},
  ) {
    if (!isMeaningfulString(value)) {
      return;
    }

    const rawString = String(value);
    const stringValue = rawString.trim();

    if (!stringValue) {
      return;
    }

    this.appendToResponse(rawString);

    if (!this.responseChunkSet.has(stringValue)) {
      this.responseChunkSet.add(stringValue);
      this.responseChunks.push(stringValue);
    }

    if (trackAsLine && !this.responseLineSet.has(stringValue)) {
      const hasDoubleNewline = /\n\s*\n/.test(rawString);
      const leadingNewline = /^\s*\n/.test(rawString);
      const bulletPattern = /^[-*•]\s+/.test(stringValue);
      const numberedPattern = /^\d+\.\s+/.test(stringValue);
      const shouldStartParagraph =
        this.responseLines.length > 0 &&
        (hasDoubleNewline ||
          leadingNewline ||
          bulletPattern ||
          numberedPattern);

      this.responseLineSet.add(stringValue);
      this.responseLines.push(stringValue);
      this.paragraphFlags.push(shouldStartParagraph);
    }
  }

  private pushTextLine(value: unknown) {
    this.pushChunk(value, {trackAsLine: true});
  }

  private ensureLineBreak() {
    if (!this.responseBuilder) {
      return;
    }

    this.responseBuilder = this.responseBuilder.replace(/[ \t]+$/g, '');

    if (!this.responseBuilder.endsWith('\n')) {
      this.responseBuilder += '\n';
    }
  }

  private ensureParagraphBreak() {
    if (!this.responseBuilder) {
      return;
    }

    this.responseBuilder = this.responseBuilder.replace(/[ \t]+$/g, '');
    this.responseBuilder = this.responseBuilder.replace(/\n+$/g, '');
    this.responseBuilder += '\n\n';
  }

  private shouldInsertImplicitSpace(nextToken: string) {
    if (!this.responseBuilder) {
      return false;
    }

    const lastChar = this.responseBuilder.slice(-1);
    if (!lastChar || /\s/.test(lastChar) || lastChar === '\n') {
      return false;
    }

    const firstChar = nextToken[0] ?? '';
    if (!firstChar) {
      return false;
    }

    // Always insert after sentence/phrase punctuation when the next token is text.
    if (/[.?!:;,)]/.test(lastChar) && /[A-Za-z0-9]/.test(firstChar)) {
      return true;
    }

    // If the next token begins with markdown-like punctuation, keep a boundary.
    if (/[A-Za-z0-9]/.test(lastChar) && /[*_[`]/.test(firstChar)) {
      return true;
    }

    // Insert between letters and digits (e.g. "coach" + "1200").
    if (/[A-Za-z]/.test(lastChar) && /\d/.test(firstChar)) {
      return true;
    }
    if (/\d/.test(lastChar) && /[A-Za-z]/.test(firstChar)) {
      return true;
    }

    // Insert at camel-case boundaries (e.g. "2025." + "Coach").
    if (/[a-z]/.test(lastChar) && /[A-Z]/.test(firstChar)) {
      return true;
    }

    // Insert before common standalone words when upstream deltas omit leading spaces.
    const word = nextToken.match(/^[A-Za-z]+/)?.[0]?.toLowerCase() ?? '';
    if (word && AbacusStreamParser.IMPLICIT_SPACE_WORDS.has(word)) {
      return true;
    }

    return false;
  }

  private appendNormalizedText(rawText: string) {
    if (!rawText) {
      return;
    }

    const hasLeadingSpace = /^\s+/.test(rawText);
    const hasTrailingSpace = /\s+$/.test(rawText);
    const normalized = rawText.replace(/\s+/g, ' ').trim();

    if (!normalized) {
      return;
    }

    const startsWithBullet =
      /^[-*•]\s+/.test(normalized) || /^\d+\.\s+/.test(normalized);

    if (
      startsWithBullet &&
      this.responseBuilder &&
      !this.responseBuilder.endsWith('\n')
    ) {
      this.ensureLineBreak();
    }

    // Preserve spaces as provided by the stream. Do not infer spaces between
    // chunks, because streamed deltas can be mid-token (e.g. "a" + "ardvark").
    if (
      hasLeadingSpace &&
      this.responseBuilder &&
      !/\s$/.test(this.responseBuilder)
    ) {
      this.responseBuilder += ' ';
    } else if (this.shouldInsertImplicitSpace(normalized)) {
      this.responseBuilder += ' ';
    }

    this.responseBuilder += normalized;

    if (hasTrailingSpace && !this.responseBuilder.endsWith(' ')) {
      this.responseBuilder += ' ';
    }
  }

  private appendToResponse(rawString: string) {
    if (!rawString) {
      return;
    }

    const comparableIncoming = this.normalizeComparable(rawString);
    if (!comparableIncoming) {
      return;
    }

    const comparableExisting = this.responseComparable;
    const remainderComparable = this.computeRemainder(
      comparableExisting,
      comparableIncoming,
    );

    if (!remainderComparable.trim()) {
      return;
    }

    const paragraphSegments = remainderComparable.split(/\n{2,}/);

    paragraphSegments.forEach((segment, segmentIndex) => {
      if (segmentIndex > 0) {
        this.ensureParagraphBreak();
      }

      const lines = segment.split(/\n/);
      lines.forEach((line, lineIndex) => {
        if (lineIndex > 0) {
          this.ensureLineBreak();
        }

        this.appendNormalizedText(line);
      });
    });

    this.responseComparable =
      `${comparableExisting}${remainderComparable}`.trim();
  }

  /**
   * The upstream stream may deliver either:
   * - deltas (token fragments), or
   * - cumulative text-so-far.
   *
   * We must be conservative with "overlap" de-duplication because small
   * overlaps (e.g. "a" + "ardvark") are common with deltas and should NOT
   * drop characters.
   */
  private computeRemainder(existing: string, incoming: string) {
    if (!incoming) {
      return '';
    }

    if (!existing) {
      return incoming;
    }

    // Some streams repeat a numeric token across chunk boundaries, e.g.
    // "4" then " 4 orders" or "10" then " 10 orders". If the incoming chunk
    // starts with the same number (optionally after whitespace) and that number
    // is not immediately followed by another digit, drop it from the remainder.
    const existingTrailingNumber = existing.match(/(\d+)$/)?.[1] ?? '';
    const incomingLeadingNumberMatch = incoming.match(/^(\s*)(\d+)(?!\d)/);
    if (
      existingTrailingNumber &&
      incomingLeadingNumberMatch &&
      incomingLeadingNumberMatch[2] === existingTrailingNumber
    ) {
      const leadingWhitespace = incomingLeadingNumberMatch[1] ?? '';
      const digits = incomingLeadingNumberMatch[2] ?? '';
      const afterNumber = incoming.slice(
        leadingWhitespace.length + digits.length,
      );
      if (!afterNumber) {
        return '';
      }

      if (/^\s/.test(afterNumber)) {
        return afterNumber;
      }

      if (leadingWhitespace) {
        return ` ${afterNumber}`;
      }
    }

    // Cumulative: incoming contains the full assembled text.
    const existingLooksSubstantial =
      existing.length >= 4 || /\s/.test(existing);
    if (existingLooksSubstantial && incoming.startsWith(existing)) {
      return incoming.slice(existing.length);
    }

    // Duplicate (rare): incoming is fully contained at the end of existing.
    if (existing.endsWith(incoming)) {
      return '';
    }

    // Overlap de-duplication is only safe when the overlap is "substantial"
    // or clearly on a boundary. Otherwise, it can drop real characters.
    const overlap = this.findOverlap(existing, incoming);
    if (overlap <= 0) {
      return incoming;
    }

    // Special-case: 1-char overlaps are usually unsafe (mid-token splits), but a
    // shared digit that continues with a non-digit is likely a repeated token.
    if (overlap === 1) {
      const shared = incoming[0] ?? '';
      const next = incoming[1] ?? '';
      if (/\d/.test(shared) && next && !/\d/.test(next)) {
        return incoming.slice(1);
      }
    }

    const MIN_SAFE_OVERLAP = 8;
    if (overlap >= MIN_SAFE_OVERLAP) {
      return incoming.slice(overlap);
    }

    if (overlap >= 2) {
      const charBeforeOverlap = existing[existing.length - overlap - 1] ?? '';
      const charAfterOverlap = incoming[overlap] ?? '';
      const boundaryBefore =
        !charBeforeOverlap || /\s|[([{]/.test(charBeforeOverlap);
      const boundaryAfter =
        !charAfterOverlap || /[\s,.;:!?)]/.test(charAfterOverlap);

      if (boundaryBefore || boundaryAfter) {
        return incoming.slice(overlap);
      }
    }

    return incoming;
  }

  private normalizeComparable(value: string) {
    return (
      value
        .replace(/\r/g, '')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        // Preserve a single trailing space so word-boundary whitespace isn't lost
        // when upstream sends it as the end of a delta chunk (e.g. "Hello ").
        .replace(/[ \t]+$/g, ' ')
    );
  }

  private findOverlap(existing: string, incoming: string) {
    const maxOverlap = Math.min(existing.length, incoming.length);
    for (let i = maxOverlap; i >= 0; i -= 1) {
      if (existing.slice(-i) === incoming.slice(0, i)) {
        return i;
      }
    }
    return 0;
  }

  private collectFromSource(source: any) {
    if (source == null) {
      return;
    }

    if (!isObject(source)) {
      this.pushChunk(source, {trackAsLine: true});
      if (isMeaningfulString(source)) {
        this.assignIfMissing('response', source);
      }
      return;
    }

    if (source.ping) {
      return;
    }

    if (source.result && typeof source.result === 'object') {
      this.assignIfMissing('result', source.result);
      this.collectFromSource(source.result);
    }

    const sourceText = isMeaningfulString(source.text) ? source.text : null;
    const sourceDelta = isMeaningfulString(source.delta) ? source.delta : null;
    const sourceContent = isMeaningfulString(source.content)
      ? source.content
      : null;
    const sourceResponse = isMeaningfulString(source.response)
      ? source.response
      : null;
    const sourceFinalResponse = isMeaningfulString(source.finalResponse)
      ? source.finalResponse
      : null;

    if (sourceText) {
      this.pushTextLine(sourceText);
    }
    if (sourceDelta) {
      this.pushTextLine(sourceDelta);
    }
    this.pushChunk(source.content, {trackAsLine: true});
    this.pushChunk(source.response, {trackAsLine: true});
    this.pushChunk(source.finalResponse, {trackAsLine: true});

    const conversationId =
      source.conversationId ??
      source.conversationID ??
      source.conversation_id ??
      source.sessionId ??
      source.sessionID ??
      source.session_id ??
      null;

    this.assignIfMissing('conversationId', conversationId);
    this.assignIfMissing('conversationID', conversationId);

    const deploymentConversationId =
      source.deploymentConversationId ??
      source.deploymentConversationID ??
      source.deployment_conversation_id ??
      null;

    this.assignIfMissing('deploymentConversationId', deploymentConversationId);

    const externalSessionId =
      source.externalSessionId ??
      source.externalSessionID ??
      source.external_session_id ??
      null;

    this.assignIfMissing('externalSessionId', externalSessionId);

    this.assignArrayIfUseful('messages', source.messages);
    this.assignArrayIfUseful(
      'messages',
      (source.result && Array.isArray(source.result.messages)
        ? source.result.messages
        : null) as any,
    );
    this.assignArrayIfUseful('segments', source.segments);
    this.assignArrayIfUseful(
      'segments',
      (source.result && Array.isArray(source.result.segments)
        ? source.result.segments
        : null) as any,
    );
    this.assignArrayIfUseful(
      'searchResults',
      source.searchResults ?? source.results ?? source.documents,
    );

    const responseCandidate =
      source.finalResponse ??
      source.response ??
      source.message ??
      source.text ??
      source.answer ??
      source.content ??
      source.value ??
      source.displayText ??
      source.body ??
      source.bodyText ??
      source.output ??
      source.snippet ??
      null;

    if (isMeaningfulString(responseCandidate)) {
      // Avoid pushing the same payload twice (e.g. `delta` is already handled
      // above, but some providers also surface it in a response-like field).
      if (
        responseCandidate !== sourceText &&
        responseCandidate !== sourceDelta &&
        responseCandidate !== sourceContent &&
        responseCandidate !== sourceResponse &&
        responseCandidate !== sourceFinalResponse
      ) {
        this.pushChunk(responseCandidate, {trackAsLine: true});
      }
      this.assignIfMissing('response', responseCandidate);
    }

    this.assignIfMissing('conversationStepId', source.conversationStepId);
    this.assignIfMissing('completionReason', source.completionReason);
    this.assignIfMissing('tokenUsage', source.tokenUsage);

    if (source.payload) {
      this.collectFromSource(source.payload);
    }

    if (source.data) {
      this.collectFromSource(source.data);
    }
  }

  private assignIfMissing(key: string, value: unknown) {
    if (value == null) {
      return;
    }

    if (typeof value === 'string' && value.trim().length === 0) {
      return;
    }

    if (!(key in this.aggregated) || this.aggregated[key] == null) {
      this.aggregated[key] = value;
    }
  }

  private assignArrayIfUseful(key: string, value: unknown) {
    if (!Array.isArray(value) || value.length === 0) {
      return;
    }

    if (
      !Array.isArray(this.aggregated[key]) ||
      this.aggregated[key].length === 0
    ) {
      this.aggregated[key] = value;
    }
  }

  private buildAggregated(): AggregatedResult {
    const aggregated: AggregatedResult = {
      ...this.aggregated,
      raw: this.raw,
      events: this.events.slice(),
    };

    const assembledResponse = this.responseBuilder.trim();
    if (assembledResponse) {
      aggregated.response = assembledResponse;
      aggregated.responseParagraphs = assembledResponse.split(/\n{2,}/);
      aggregated.responseLines = assembledResponse.split(/\n/);
    }

    if (!assembledResponse && this.responseLines.length > 0) {
      const paragraphs: string[] = [];

      this.responseLines.forEach((line, index) => {
        if (index === 0 || this.paragraphFlags[index]) {
          paragraphs.push(line);
        } else if (paragraphs.length === 0) {
          paragraphs.push(line);
        } else {
          paragraphs[paragraphs.length - 1] = `${
            paragraphs[paragraphs.length - 1]
          } ${line}`;
        }
      });

      const combinedText =
        paragraphs.length > 0
          ? paragraphs.join('\n\n')
          : this.responseLines.join(' ');

      aggregated.responseLines = this.responseLines.slice();
      aggregated.responseParagraphs = paragraphs;
      aggregated.response = combinedText;
    } else if (!aggregated.response && this.responseChunks.length > 0) {
      aggregated.response = this.responseChunks.join('');
    }

    if (this.responseChunks.length > 0) {
      aggregated.responseChunks = this.responseChunks.slice();
    }

    return aggregated;
  }
}

export const parseStreamingAbacusResponse = (raw: string) => {
  const parser = new AbacusStreamParser();
  parser.ingest(raw);
  return parser.finalize();
};
